import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  message,
  Space,
  Popconfirm,
  Card,
  Pagination,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { apiClient } from '../../../config/apiClient';
import MainLayout from '../../layout/layout';
import { usePermissions } from '../../../context/permissionsContext';

const { TextArea } = Input;

const Empleados = () => {
  const { hasPermission } = usePermissions();
  const [empleados, setEmpleados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingEmpleado, setEditingEmpleado] = useState(null);
  const [form] = Form.useForm();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [filtroNombre, setFiltroNombre] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const loadEmpleados = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/gestion/empleados');
      if (response.ok) {
        const data = await response.json();
        setEmpleados(data);
        setPagination((prev) => ({ ...prev, total: data.length }));
      } else {
        message.error('Error al cargar los empleados');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar los empleados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEmpleados();
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const empleadosFiltrados = empleados.filter((emp) =>
    !filtroNombre || emp.emp_nombre?.toLowerCase().includes(filtroNombre.toLowerCase())
  );

  const empleadosPaginados = empleadosFiltrados.slice(
    (pagination.current - 1) * pagination.pageSize,
    pagination.current * pagination.pageSize
  );

  const handleOpenModal = (empleado = null) => {
    setEditingEmpleado(empleado);
    if (empleado) {
      form.setFieldsValue({
        nombre: empleado.emp_nombre,
        direccion: empleado.emp_direccion,
        telefono: empleado.emp_telefono,
        mail: empleado.emp_mail,
        localidad: empleado.emp_localidad,
        observaciones: empleado.emp_observaciones,
      });
    } else {
      form.resetFields();
    }
    setModalVisible(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (editingEmpleado) {
        const response = await apiClient.put(
          `/gestion/empleados/${editingEmpleado.emp_id}`,
          values
        );
        if (response.ok) {
          message.success('Empleado actualizado correctamente');
          setModalVisible(false);
          loadEmpleados();
        } else {
          const error = await response.json();
          message.error(error.message || 'Error al actualizar el empleado');
        }
      } else {
        const response = await apiClient.post('/gestion/empleados', values);
        if (response.ok) {
          message.success('Empleado creado correctamente');
          setModalVisible(false);
          loadEmpleados();
        } else {
          const error = await response.json();
          message.error(error.message || 'Error al crear el empleado');
        }
      }
    } catch (error) {
      if (error.errorFields) return;
      console.error('Error:', error);
      message.error('Error al guardar el empleado');
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await apiClient.delete(`/gestion/empleados/${id}`);
      if (response.ok) {
        message.success('Empleado eliminado correctamente');
        loadEmpleados();
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al eliminar el empleado');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al eliminar el empleado');
    }
  };

  const columns = [
    { title: 'Nombre', dataIndex: 'emp_nombre', key: 'emp_nombre' },
    {
      title: 'Localidad',
      dataIndex: 'emp_localidad',
      key: 'emp_localidad',
      render: (value) => value || '-',
    },
    {
      title: 'Teléfono',
      dataIndex: 'emp_telefono',
      key: 'emp_telefono',
      render: (value) => value || '-',
    },
    {
      title: 'Mail',
      dataIndex: 'emp_mail',
      key: 'emp_mail',
      render: (value) => value || '-',
    },
    ...((hasPermission('empleados', 'editar') || hasPermission('empleados', 'eliminar'))
      ? [{
          title: 'Acciones',
          key: 'acciones',
          width: 150,
          render: (_, record) => (
            <Space>
              {hasPermission('empleados', 'editar') && (
                <Button
                  type="link"
                  icon={<EditOutlined />}
                  onClick={() => handleOpenModal(record)}
                >
                  Editar
                </Button>
              )}
              {hasPermission('empleados', 'eliminar') && (
                <Popconfirm
                  title="¿Estás seguro de eliminar este empleado?"
                  onConfirm={() => handleDelete(record.emp_id)}
                  okText="Sí"
                  cancelText="No"
                >
                  <Button type="link" danger icon={<DeleteOutlined />}>
                    Eliminar
                  </Button>
                </Popconfirm>
              )}
            </Space>
          ),
        }]
      : []),
  ];

  return (
    <MainLayout>
      <div style={{ padding: '24px' }}>
        <div style={{ marginBottom: 16 }}>
          <h1 style={{ margin: 0, marginBottom: 16, color: '#ffffff', fontSize: isMobile ? '20px' : '24px' }}>
            Gestión de Empleados
          </h1>
          <Space wrap style={{ marginBottom: 16 }}>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadEmpleados}
              loading={loading}
            >
              {isMobile ? null : 'Actualizar'}
            </Button>
            {hasPermission('empleados', 'crear') && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => handleOpenModal(null)}
              >
                Nuevo Empleado
              </Button>
            )}
          </Space>

          <div style={{ marginBottom: 16, padding: 16, backgroundColor: '#2d2d2d', borderRadius: 8 }}>
            <Input
              placeholder="Buscar empleado por nombre..."
              value={filtroNombre}
              onChange={(e) => {
                setFiltroNombre(e.target.value);
                setPagination((prev) => ({ ...prev, current: 1 }));
              }}
              allowClear
              style={{ maxWidth: 400 }}
            />
          </div>
        </div>

        {isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {empleadosPaginados.map((empleado) => (
              <Card key={empleado.emp_id} hoverable style={{ borderLeft: '4px solid #1890ff', borderRadius: '8px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div>
                    <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '8px', color: '#ffffff' }}>
                      {empleado.emp_nombre}
                    </div>
                    <div style={{ fontSize: '13px', color: '#b0b0b0', marginBottom: '4px' }}>
                      Localidad: {empleado.emp_localidad || '-'}
                    </div>
                    <div style={{ fontSize: '13px', color: '#b0b0b0', marginBottom: '4px' }}>
                      Teléfono: {empleado.emp_telefono || '-'}
                    </div>
                    <div style={{ fontSize: '13px', color: '#b0b0b0', marginBottom: '4px' }}>
                      Mail: {empleado.emp_mail || '-'}
                    </div>
                  </div>
                  {(hasPermission('empleados', 'editar') || hasPermission('empleados', 'eliminar')) && (
                    <Space wrap>
                      {hasPermission('empleados', 'editar') && (
                        <Button
                          type="link"
                          icon={<EditOutlined />}
                          onClick={() => handleOpenModal(empleado)}
                        >
                          Editar
                        </Button>
                      )}
                      {hasPermission('empleados', 'eliminar') && (
                        <Popconfirm
                          title="¿Estás seguro de eliminar este empleado?"
                          onConfirm={() => handleDelete(empleado.emp_id)}
                          okText="Sí"
                          cancelText="No"
                        >
                          <Button type="link" danger icon={<DeleteOutlined />}>
                            Eliminar
                          </Button>
                        </Popconfirm>
                      )}
                    </Space>
                  )}
                </div>
              </Card>
            ))}
            {empleadosFiltrados.length > 0 && (
              <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
                <Pagination
                  current={pagination.current}
                  pageSize={pagination.pageSize}
                  total={empleadosFiltrados.length}
                  onChange={(page, pageSize) => {
                    setPagination((prev) => ({ ...prev, current: page, pageSize }));
                  }}
                  showSizeChanger
                  pageSizeOptions={['10', '20', '50', '100']}
                  showTotal={(total) => `Total: ${total} empleados`}
                />
              </div>
            )}
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={empleadosPaginados}
            loading={loading}
            rowKey="emp_id"
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: empleadosFiltrados.length,
              showSizeChanger: true,
              showTotal: (total) => `Total: ${total} empleados`,
              pageSizeOptions: ['10', '20', '50', '100'],
              onChange: (page, pageSize) => {
                setPagination((prev) => ({ ...prev, current: page, pageSize }));
              },
            }}
          />
        )}

        <Modal
          title={editingEmpleado ? 'Editar Empleado' : 'Nuevo Empleado'}
          open={modalVisible}
          onOk={handleSave}
          onCancel={() => {
            setModalVisible(false);
            form.resetFields();
          }}
          okText="Guardar"
          cancelText="Cancelar"
          width={600}
        >
          <Form form={form} layout="vertical">
            <Form.Item
              name="nombre"
              label="Nombre"
              rules={[{ required: true, message: 'El nombre es requerido' }]}
            >
              <Input placeholder="Nombre del empleado" />
            </Form.Item>

            <Form.Item name="direccion" label="Dirección">
              <Input placeholder="Dirección" />
            </Form.Item>

            <Form.Item name="telefono" label="Teléfono">
              <Input placeholder="Teléfono" />
            </Form.Item>

            <Form.Item
              name="mail"
              label="Mail"
              rules={[{ type: 'email', message: 'Mail inválido' }]}
            >
              <Input placeholder="mail@ejemplo.com" />
            </Form.Item>

            <Form.Item name="localidad" label="Localidad">
              <Input placeholder="Localidad" />
            </Form.Item>

            <Form.Item name="observaciones" label="Observaciones">
              <TextArea rows={3} placeholder="Observaciones" />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default Empleados;
